import React from "react";
import { Box, Grid, Chip } from "@mui/material";
import {
  InfoOutlined,
  LocalShippingOutlined,
  DateRangeOutlined,
  DirectionsCarFilledOutlined,
  ManageAccountsOutlined,
} from "@mui/icons-material";

import { useShipmentFulfillmentDetailContext } from "contexts/ShipmentFulfillmentContext";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";

import { localizeDateTimeShort } from "utils/dateFormats";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import { useAuth } from "hooks";

const statusStyles = {
  Completed: {
    backgroundColor: "#D4EDDA",
    hoverBackgroundColor: "#C3E6CB",
    textColor: "#155724",
  },
  Cancelled: {
    backgroundColor: "#FADBD8",
    hoverBackgroundColor: "#F5B7B1",
    textColor: "#C0392B",
  },
  default: {
    backgroundColor: "#E9ECEF",
    hoverBackgroundColor: "#D6D8DB",
    textColor: "#6C757D",
  },
};

const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const CollectionBasicDetail = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const { fulfillmentDetail, loadingFulfillment } =
    useShipmentFulfillmentDetailContext();

  return !loadingFulfillment ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        {/* Identifiers */}
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="Collection ID"
            subtitle={fulfillmentDetail?.collectionId}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <DirectionsCarFilledOutlined color="primary" />}
            title="Assigned Vehicle"
            subtitle={
              fulfillmentDetail?.vehicle_id &&
              typeof fulfillmentDetail.vehicle_id === "object" ? (
                isCustomer || isVendor || isContractor || isDriver ? (
                  `${fulfillmentDetail.vehicle_id.vehicle_id || ""} - ${
                    fulfillmentDetail.vehicle_id.registration_number || ""
                  }`
                ) : (
                  <OpenInNewTab
                    label={`${
                      fulfillmentDetail.vehicle_id.vehicle_id || ""
                    } - ${
                      fulfillmentDetail.vehicle_id.registration_number || ""
                    }`}
                    link={`/app/crm/vehicle/${fulfillmentDetail.vehicle_id._id}/detail`}
                  />
                )
              ) : (
                "N/A"
              )
            }
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title="Assigned Driver"
            subtitle={
              fulfillmentDetail?.driverId &&
              typeof fulfillmentDetail.driverId === "object" ? (
                isCustomer || isVendor || isContractor || isDriver ? (
                  `${fulfillmentDetail.driverId.driverId || ""} - ${
                    fulfillmentDetail.driverId.name || ""
                  }`
                ) : (
                  <OpenInNewTab
                    label={`${fulfillmentDetail.driverId.driverId || ""} - ${
                      fulfillmentDetail.driverId.name || ""
                    }`}
                    link={`/app/crm/driver/${fulfillmentDetail.driverId._id}/detail`}
                  />
                )
              ) : (
                "N/A"
              )
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title="Contractor"
            subtitle={
              fulfillmentDetail?.contractorId &&
              typeof fulfillmentDetail.contractorId === "object" ? (
                isCustomer || isVendor || isContractor || isDriver ? (
                  `${fulfillmentDetail.contractorId.contractorId || ""} - ${
                    fulfillmentDetail.contractorId.name || ""
                  }`
                ) : (
                  <OpenInNewTab
                    label={`${
                      fulfillmentDetail.contractorId.contractorId || ""
                    } - ${fulfillmentDetail.contractorId.name || ""}`}
                    link={`/app/crm/contractor/${fulfillmentDetail.contractorId._id}/detail`}
                  />
                )
              ) : (
                "N/A"
              )
            }
          />
        </Grid>
        {/* Timestamps */}
        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title="Pickup Time"
            subtitle={
              fulfillmentDetail?.actualPickupTime
                ? localizeDateTimeShort(fulfillmentDetail.actualPickupTime)
                : "N/A"
            }
          />
        </Grid>
  <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Created By"
            subtitle={fulfillmentDetail?.createdBy?.name	 || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title="Created At"
            subtitle={
              fulfillmentDetail?.createdAt
                ? localizeDateTimeShort(fulfillmentDetail.createdAt)
                : "N/A"
            }
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title="Updated At"
            subtitle={
              fulfillmentDetail?.updatedAt
                ? localizeDateTimeShort(fulfillmentDetail.updatedAt)
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Active"
            subtitle={fulfillmentDetail?.isActive ? "Active" : "Inactive"}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Status"
            subtitle={
              <Box
                display="flex"
                flexDirection="column"
                alignItems="start"
                mt={1}
              >
                <Chip
                  label={fulfillmentDetail?.status || "Unknown"}
                  style={{
                    backgroundColor: getStatusStyle(fulfillmentDetail?.status)
                      .backgroundColor,
                    color: getStatusStyle(fulfillmentDetail?.status).textColor,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                    height: "20px",
                    padding: "0 6px",
                    borderRadius: "12px",
                    marginTop: "4px",
                    width: "auto",
                  }}
                />
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default CollectionBasicDetail;
